import React, { useState, useEffect } from 'react';

export function Calculator() {
  const [price, setPrice] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [residualValue, setResidualValue] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [fee, setFee] = useState('');
  const [months, setMonths] = useState('48');
  const [monthlyPayment, setMonthlyPayment] = useState<number | null>(null);

  useEffect(() => {
    calculateMonthlyPayment();
  }, [price, downPayment, residualValue, interestRate, months]);

  const calculateMonthlyPayment = () => {
    if (!price || !downPayment || !residualValue || !interestRate || !fee) {
      setMonthlyPayment(null);
      return;
    }

    // Parse input values
    const carPrice = parseFloat(price);
    const initialPayment = parseFloat(downPayment);
    const residualVal = parseFloat(residualValue);
    const periodMonths = parseInt(months);
    const annualRate = parseFloat(interestRate.replace(',', '.'));
    const feeAmount = parseFloat(fee);

    // Validate inputs
    if (isNaN(carPrice) || isNaN(initialPayment) || isNaN(residualVal) || 
        isNaN(periodMonths) || isNaN(annualRate) || isNaN(feeAmount)) {
      setMonthlyPayment(null);
      return;
    }

    // Calculate financing amount
    const financingAmount = carPrice + feeAmount - initialPayment;

    // Convert annual rate to quarterly rate first
    const quarterlyRate = annualRate / 100 / 4;
    
    // Convert to monthly rate using the formula: (1 + quarterly_rate)^(1/3) - 1
    const monthlyRate = quarterlyRate / 3;
    
    // Use Excel's PMT formula for payment at start of period (type = 1)
    const payment = (financingAmount - (residualVal / Math.pow(1 + monthlyRate, periodMonths))) *
      (monthlyRate * Math.pow(1 + monthlyRate, periodMonths)) /
      (Math.pow(1 + monthlyRate, periodMonths) - 1);

    setMonthlyPayment(Math.round(payment));
  };

  const formatNumber = (value: string) => {
    return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Leasing Beregner</h1>
      </div>
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bilens pris
            </label>
            <div className="relative">
              <input
                type="text"
                value={formatNumber(price)}
                onChange={(e) => setPrice(e.target.value.replace(/\./g, ''))}
                className="w-full p-2 pr-8 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Førstegangsydelse
            </label>
            <div className="relative">
              <input
                type="text"
                value={formatNumber(downPayment)}
                onChange={(e) => setDownPayment(e.target.value.replace(/\./g, ''))}
                className="w-full p-2 pr-8 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scrapværdi
            </label>
            <div className="relative">
              <input
                type="text"
                value={formatNumber(residualValue)}
                onChange={(e) => setResidualValue(e.target.value.replace(/\./g, ''))}
                className="w-full p-2 pr-8 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Årlig rente
            </label>
            <div className="relative">
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="w-full p-2 pr-8 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
                step="0.1"
                min="0"
                max="100"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gebyr
            </label>
            <div className="relative mb-4">
              <input
                type="text"
                value={formatNumber(fee)}
                onChange={(e) => setFee(e.target.value.replace(/\./g, ''))}
                className="w-full p-2 pr-8 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="5000"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
            </div>
            <div className="text-xs text-gray-500">
              Finansieringsbeløb: {formatNumber((price && fee && downPayment) ? 
                (parseFloat(price.replace(/\./g, '')) + parseFloat(fee.replace(/\./g, '')) - parseFloat(downPayment.replace(/\./g, ''))).toString() 
                : '0')} kr
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Løbetid i måneder
            </label>
            <div className="relative">
              <input
                type="number"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                className="w-full p-2 pr-8 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="48"
                min="1"
                max="120"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">mdr</span>
            </div>
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <div className="text-center">
            <label className="block text-sm font-medium text-blue-700 mb-1">
              Månedlig leasingydelse
            </label>
            <div className="text-3xl font-bold text-blue-700">
              {monthlyPayment ? `${formatNumber(monthlyPayment.toString())} kr` : '- kr'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}